import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";
import { useAuth } from "@clerk/clerk-react";
import { deriveCurrencyFromCountry, type CurrencyCode } from "@/lib/currency";

// Response: { country: "<ISO-3166-1 alpha-2>" } or { country: null } or null
const geoCountryResponseSchema = z.union([
    z.object({ country: z.string().length(2).optional().nullable() }),
    z.null(),
]);

export type GeoCountryResponse = z.infer<typeof geoCountryResponseSchema>;
export type GeoData = { country: string | null; currency: CurrencyCode };


export const useGeolocation = (options?: any) => {
    const { getToken } = useAuth();
    return useQuery<GeoData>({
        queryKey: ["geo", "country"],
        queryFn: async () => {
            const token = await getToken();
            // If no token yet (e.g. pre-auth), avoid hitting a protected endpoint and return defaults
            if (!token) {
                const currency = deriveCurrencyFromCountry(null);
                return { country: null, currency };
            }
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/geo/country`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const parsed = geoCountryResponseSchema.parse(response.data);
            const country = (parsed && typeof parsed === "object") ? (parsed.country ?? null) : null;
            const normalized = country ? country.toUpperCase() : null;
            const currency = deriveCurrencyFromCountry(normalized);
            return { country: normalized, currency };
        },
        staleTime: 5 * 60 * 1000,
        ...options,
    });
};


